import { App } from '@capacitor/app';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { isHomePath, isMobile, persistRoute, readPersistedRoute } from '@sophocles/app/routePersistence';

/**
 * Wires up mobile route persistence:
 *
 * - on mount, captures the saved route from storage *synchronously*
 *   so the inevitable `/` → home redirect that fires during the
 *   first render cannot overwrite it before we have a chance to
 *   restore.
 * - saves the current path to storage on every navigation, but only
 *   *after* the restore decision has been made.
 * - saves on Capacitor ``appStateChange`` becoming inactive
 *   (defense in depth — covers paths the location effect might
 *   miss because the user navigated and immediately backgrounded).
 * - on cold boot once auth is ready, if the user has landed on a
 *   "home" route but we have a saved deeper route, navigate to
 *   that route. RequireAuth's guards then run against the deep
 *   path rather than us leaving the user on home.
 *
 * Called from MainLayout so it lives inside the BrowserRouter.
 */
export default function useRoutePersistence(authReady) {
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);

  // Capture the saved route on first render, before *any* effects
  // (including the / -> home redirect handler) have a chance to
  // run. Without this capture, the persist effect below would race
  // the restore effect and overwrite the saved deep path with
  // whichever home route the cold boot landed on.
  const savedRouteRef = useRef(null);
  if (savedRouteRef.current === null) {
    savedRouteRef.current = readPersistedRoute() || '';
  }

  // Gate the persist effect on a state flag flipped by the restore
  // effect. State (not ref) so the persist effect re-runs once we
  // flip it, even if the location hasn't changed.
  const [readyToPersist, setReadyToPersist] = useState(false);

  // 1. Save on every navigation, after restore decision.
  useEffect(() => {
    if (!readyToPersist) return;
    persistRoute(location.pathname, location.search);
  }, [location.pathname, location.search, readyToPersist]);

  // 2. Save on backgrounding. Capacitor App listener.
  useEffect(() => {
    if (!isMobile()) return undefined;
    const handleAppStateChange = state => {
      if (!state.isActive) {
        // Read directly from window.location — React state may have
        // been unmounted by the time the OS pauses us, but
        // window.location is current.
        persistRoute(window.location.pathname, window.location.search);
      }
    };
    const subscription = App.addListener('appStateChange', handleAppStateChange);
    return () => {
      // Capacitor 5+ returns a Promise<PluginListenerHandle>.
      Promise.resolve(subscription).then(handle => {
        if (handle && typeof handle.remove === 'function') handle.remove();
      });
    };
  }, []);

  // 3. Restore once auth is ready, if we landed on a home route.
  useEffect(() => {
    if (!isMobile()) {
      // Web: nothing to restore, but still allow persist to run
      // (cheap, scoped to mobile internally so a no-op anyway).
      if (!readyToPersist) setReadyToPersist(true);
      return;
    }
    if (!authReady) return;
    if (restoredRef.current) return;

    // Only restore if the cold boot landed on a home route. If the
    // user is already deep, respect that — they navigated explicitly
    // (or iOS preserved the URL itself).
    if (!isHomePath(location.pathname)) {
      restoredRef.current = true;
      setReadyToPersist(true);
      return;
    }

    const saved = savedRouteRef.current;
    restoredRef.current = true;
    if (!saved || saved === `${location.pathname}${location.search}`) {
      setReadyToPersist(true);
      return;
    }

    // Replace so the home path doesn't end up in history.
    navigate(saved, { replace: true });
    setReadyToPersist(true);
  }, [authReady, location.pathname, location.search, navigate, readyToPersist]);
}
